import { ModalAssinatura } from "@/components/ModalAssinatura";
import { useServidor } from "@/hooks/useServidor";
import { IFormValuesRelatorioAM } from "@/interfaces/IFormValuesRelatorioAM";
import { cpfMask } from "@/utils/cpfMask";
import {
  CheckOutlined,
  ClockCircleOutlined,
  EditOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  App,
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useRef, useState } from "react";
import ReactSignatureCanvas from "react-signature-canvas";

const statusOptions = [
  { label: "Assinado Digitalmente", value: "ASSINADO_DIGITALMENTE" },
  { label: "Assinado Fisicamente", value: "ASSINADO_FISICAMENTE" },
  { label: "Impossibilitado", value: "IMPOSSIBILITADO" },
  { label: "Ausente", value: "AUSENTE" },
  { label: "Recusado", value: "RECUSADO" },
  { label: "Não Alfabetizado", value: "NAO_ALFABETIZADO" },
] as const;

export function AssinaturaRelatorioAM({
  form,
}: {
  form: FormInstance<IFormValuesRelatorioAM>;
}) {
  const assinantes = Form.useWatch("assinaturas", form) || [];
  const [modalVisible, setModalVisible] = useState(false);
  const [currentAssinante, setCurrentAssinante] = useState<number | null>(null);
  const refAssinante = useRef<ReactSignatureCanvas>(null);
  const { message } = App.useApp();
  const { listaServidores } = useServidor();

  const handleOpenModal = (index: number) => {
    setCurrentAssinante(index);
    setModalVisible(true);

    const assinaturaAtual = assinantes[index]?.assinatura;
    if (assinaturaAtual && refAssinante.current) {
      refAssinante.current.fromDataURL(assinaturaAtual);
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setCurrentAssinante(null);

    if (currentAssinante === null) return;

    if (refAssinante.current?.isEmpty()) {
      form.setFieldValue(["assinaturas", currentAssinante, "assinatura"], null);
      return;
    }

    form.setFieldValue(
      ["assinaturas", currentAssinante, "assinatura"],
      refAssinante.current?.toDataURL("image/png")
    );

    refAssinante.current?.clear();
  };

  const handleCleanSignature = () => {
    refAssinante.current?.clear();
  };

  const handleSearchByMatricula = (matricula: string, index: number) => {
    const servidor = listaServidores.find((s) => s.matricula === matricula);
    if (!servidor) {
      message.error("Servidor não encontrado.");
      return;
    }

    console.log("servidor", servidor);

    form.setFieldValue(["assinaturas", index, "nome"], servidor.pessoa.nome);
    form.setFieldValue(
      ["assinaturas", index, "cpf"],
      cpfMask(servidor.pessoa.cpfCnpj)
    );
    form.setFieldValue(["assinaturas", index, "matricula"], matricula);
    form.setFieldValue(["assinaturas", index, "funcao"], servidor.funcao.nome);
  };

  return (
    <>
      <Divider orientation="left" plain>
        <Typography.Title level={5}>Assinaturas</Typography.Title>
      </Divider>

      <Form.List name="assinaturas">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => {
              const isExterno = assinantes[name]?.externo;

              return (
                <Row
                  key={key}
                  gutter={16}
                  align="middle"
                  style={{ marginBottom: 8 }}
                >
                  <Col xs={24} sm={12} md={8} lg={2}>
                    <Form.Item
                      {...restField}
                      name={[name, "externo"]}
                      valuePropName="checked"
                    >
                      <Checkbox
                        onChange={(e) => {
                          form.resetFields([
                            ["assinaturas", name, "matricula"],
                            ["assinaturas", name, "cpf"],
                            ["assinaturas", name, "nome"],
                            ["assinaturas", name, "funcao"],
                          ]);
                        }}
                      >
                        Externo
                      </Checkbox>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={4}>
                    <Form.Item
                      {...restField}
                      label="Matricula"
                      name={[name, "matricula"]}
                      rules={[
                        { required: !isExterno, message: "Campo obrigatório" },
                      ]}
                    >
                      <Input.Search
                        maxLength={10}
                        placeholder="Matricula"
                        enterButton
                        disabled={isExterno}
                        onSearch={(value) =>
                          handleSearchByMatricula(value, name)
                        }
                        onChange={(e) => {
                          const { value } = e.target;
                          form.setFieldValue(
                            ["assinaturas", name, "matricula"],
                            value.replace(/\D/g, "")
                          );

                          form.resetFields([
                            ["assinaturas", name, "cpf"],
                            ["assinaturas", name, "nome"],
                            ["assinaturas", name, "funcao"],
                          ]);
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={4}>
                    <Form.Item
                      {...restField}
                      label="CPF"
                      name={[name, "cpf"]}
                      rules={[{ required: true }]}
                    >
                      <Input
                        placeholder="CPF"
                        maxLength={14}
                        onChange={(e) => {
                          const { value } = e.target;

                          form.setFieldValue(
                            ["assinaturas", name, "cpf"],
                            cpfMask(value)
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={4}>
                    <Form.Item
                      {...restField}
                      label="Nome"
                      name={[name, "nome"]}
                      rules={[{ required: true }]}
                    >
                      <Input placeholder="Nome" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={4}>
                    <Form.Item
                      {...restField}
                      label="Função"
                      name={[name, "funcao"]}
                      rules={[{ required: true }]}
                    >
                      <Input placeholder="Função" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={3}>
                    <Form.Item
                      {...restField}
                      name={[name, "statusAssinatura"]}
                      label="Status"
                      rules={[{ required: true }]}
                    >
                      <Select
                        placeholder="Status"
                        options={statusOptions.map(({ label, value }) => ({
                          label,
                          value,
                        }))}
                        onChange={() => {
                          form.setFieldValue(
                            ["assinaturas", name, "assinatura"],
                            undefined
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Form.Item {...restField} name={[name, "assinatura"]} noStyle>
                    <Input type="hidden" />
                  </Form.Item>

                  <Col xs={24} sm={12} md={8} lg={3}>
                    <Row gutter={[8, 8]} align="middle">
                      <Col>
                        {assinantes[name]?.statusAssinatura ===
                          "ASSINADO_DIGITALMENTE" && (
                          <>
                            {assinantes[name].assinatura ? (
                              <Tag
                                color="green"
                                icon={<CheckOutlined />}
                                title="Assinado"
                              />
                            ) : (
                              <Tag
                                color="red"
                                icon={<ClockCircleOutlined />}
                                title="Assinatura Pendente"
                              />
                            )}
                          </>
                        )}

                        <Tooltip title="Assinar">
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => handleOpenModal(name)}
                            icon={<EditOutlined />}
                            disabled={
                              assinantes[name]?.statusAssinatura !==
                              "ASSINADO_DIGITALMENTE"
                            }
                          />
                        </Tooltip>
                      </Col>
                      <Col>
                        <Tooltip title="Remover Assinante">
                          <Button
                            danger
                            size="small"
                            onClick={() => remove(name)}
                            icon={<MinusOutlined />}
                            disabled={fields.length === 1}
                          />
                        </Tooltip>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              );
            })}

            <Row gutter={16} justify="center">
              <Col>
                <Button
                  onClick={() => add()}
                  type="dashed"
                  icon={<PlusOutlined />}
                >
                  Adicionar Assinante
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Form.List>

      <ModalAssinatura
        isOpen={modalVisible}
        handleClose={handleCloseModal}
        cleanSignature={handleCleanSignature}
        refAssinante={refAssinante}
        nomeAssinante={
          currentAssinante !== null
            ? assinantes[currentAssinante]?.nome
            : undefined
        }
      />
    </>
  );
}
